import React from 'react'
import { useQuery } from '@tanstack/react-query'
import styled from 'styled-components'
import ExchangeOffersView from 'src/features/stocks/components/map/ExchangeOffersView'
import {
  EXCHANGE_MAP_TYPES,
  getOfferLocationMatchKeys,
  normalizeMapOffer
} from 'src/features/stocks/components/map/offerMapUtils'
import { normalizeImportOfferDetails } from 'src/features/stocks/ducks/normalizers'
import DictionariesService from 'src/services/DictionariesService'
import ExchangeService from 'src/services/ExchangeService'

const NOOP = () => {}
const SOURCE_OFFER_CACHE_TIME = 5 * 60 * 1000

const hasRoundTripCoordinates = offer => {
  const mapOffer = normalizeMapOffer(offer, EXCHANGE_MAP_TYPES.IMPORT)

  return Boolean(
    mapOffer && mapOffer.from.mapLocation && mapOffer.to.mapLocation
  )
}

const hasRoundTripSearchData = offer => {
  const mapOffer = normalizeMapOffer(offer, EXCHANGE_MAP_TYPES.IMPORT)
  return Boolean(
    getOfferLocationMatchKeys(mapOffer.from.location).length &&
      getOfferLocationMatchKeys(mapOffer.to.location).length &&
      (mapOffer.to.rawDate || mapOffer.to.date)
  )
}

const getOfferLocationId = (offer, field) => {
  const location = offer[field]
  const rawLocation = offer.raw && offer.raw[field]

  return (
    (location && location.id) ||
    (rawLocation && rawLocation.id) ||
    offer[`${field}Id`] ||
    null
  )
}

const getRawOfferValue = (offer, field) =>
  (offer.raw && offer.raw[field]) || offer[field] || null

export const roundTripSourceOfferQueryKey = offer => [
  'round-trip-source-offer',
  offer.id,
  getOfferLocationId(offer, 'pickupLocation'),
  getOfferLocationId(offer, 'unloadingLocation'),
  getRawOfferValue(offer, 'pickupDate'),
  getRawOfferValue(offer, 'unloadingDate'),
  getRawOfferValue(offer, 'containerId'),
  getRawOfferValue(offer, 'shipownerId')
]

const setOfferLocation = (offer, field, location) =>
  typeof offer.set === 'function'
    ? offer.set(field, location)
    : { ...offer, [field]: location }

export const loadRoundTripSourceOffer = async offer => {
  if (hasRoundTripCoordinates(offer)) return offer

  let sourceOffer = offer

  try {
    const result = await ExchangeService.showImportOffer({ id: offer.id })
    sourceOffer = normalizeImportOfferDetails(result.data)
  } catch (error) {
    sourceOffer = offer
  }

  if (hasRoundTripCoordinates(sourceOffer)) return sourceOffer

  const locationFields = ['pickupLocation', 'unloadingLocation']
  const locationIds = locationFields
    .map(field => getOfferLocationId(sourceOffer, field))
    .filter(Boolean)
  if (locationIds.length === 0) return sourceOffer

  let locationsResult
  try {
    locationsResult = await DictionariesService.locations({
      filter: { id: Array.from(new Set(locationIds)) }
    })
  } catch (error) {
    return sourceOffer
  }
  const locationsById = new Map(
    (locationsResult.data || []).map(location => [
      String(location.id),
      location
    ])
  )

  return locationFields.reduce((hydratedOffer, field) => {
    const locationId = getOfferLocationId(hydratedOffer, field)
    const hydratedLocation = locationsById.get(String(locationId))

    return hydratedLocation
      ? setOfferLocation(hydratedOffer, field, hydratedLocation)
      : hydratedOffer
  }, sourceOffer)
}

export const fetchRoundTripSourceOffer = async offer => {
  const loadedOffer = await loadRoundTripSourceOffer(offer)
  // The map can resolve missing coordinates by location ID/name. Enrichment
  // must not block an otherwise usable reservation (e.g. an archived offer).
  if (
    hasRoundTripCoordinates(loadedOffer) ||
    hasRoundTripSearchData(loadedOffer)
  ) {
    return loadedOffer
  }
  if (hasRoundTripSearchData(offer)) return offer
  throw new Error('Return offer source locations or date are unavailable')
}

const ReservationRoundTripMap = ({
  className,
  isDrawerOpen = false,
  onDrawerOfferTypeChange = NOOP,
  onRoundTripOfferSelect = NOOP,
  reservation,
  selectedOfferId,
  selectedOfferType,
  setDrawerState = NOOP,
  showOffer = NOOP
}) => {
  const hasCoordinates = hasRoundTripCoordinates(reservation.offer)
  const sourceOfferQuery = useQuery({
    queryKey: roundTripSourceOfferQueryKey(reservation.offer),
    queryFn: () => fetchRoundTripSourceOffer(reservation.offer),
    enabled: !hasCoordinates,
    staleTime: SOURCE_OFFER_CACHE_TIME,
    cacheTime: SOURCE_OFFER_CACHE_TIME,
    retry: false,
    retryOnMount: true,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true
  })
  const sourceOffer =
    !hasCoordinates && sourceOfferQuery.data
      ? sourceOfferQuery.data
      : reservation.offer
  const roundTripSourceLoading =
    !hasCoordinates &&
    (sourceOfferQuery.isLoading || sourceOfferQuery.isFetching) &&
    !sourceOfferQuery.data
  const roundTripSourceError =
    !hasCoordinates &&
    sourceOfferQuery.isError &&
    !sourceOfferQuery.data &&
    !hasRoundTripSearchData(sourceOffer)

  return (
    <section className={className}>
      <MapColumn>
        <ExchangeOffersView
          compactMap
          exchangeType={EXCHANGE_MAP_TYPES.IMPORT}
          initialRoundTripOffer={sourceOffer}
          isDrawerOpen={isDrawerOpen}
          onDrawerOfferTypeChange={onDrawerOfferTypeChange}
          onRoundTripOfferSelect={onRoundTripOfferSelect}
          roundTripOnly
          roundTripSourceLoading={roundTripSourceLoading}
          roundTripSourceError={roundTripSourceError}
          selectedOfferId={selectedOfferId}
          selectedOfferType={selectedOfferType}
          setDrawerState={setDrawerState}
          showOffer={showOffer}
        />
      </MapColumn>
    </section>
  )
}

const MapColumn = styled.div`
  width: 100%;
  min-width: 0;

  > div > div {
    border: 0;
    border-radius: 0;
  }
`

export default styled(ReservationRoundTripMap)`
  width: 100%;
  margin: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.45).hex()};
`
